import React, { useState } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Checkbox, FormControlLabel, Collapse, Button, Divider, CircularProgress,
} from '@mui/material';
import { Search as SearchIcon, ExpandMore, ExpandLess } from '@mui/icons-material';

export interface FacetConfig<T> {
  key: string;
  title: string;
  keyOf: (entity: T) => string;
  labelOf?: (key: string) => string;
  iconOf?: (key: string) => React.ReactNode;
  order?: string[];
  advanced?: boolean;
}

export interface FacetFilterState {
  search: string;
  selections: Record<string, string[]>;
}

export const emptyFacetState = (): FacetFilterState => ({ search: '', selections: {} });

const selectionOf = (state: FacetFilterState, key: string): string[] => state.selections[key] ?? [];

/** Filter entities by the search query and every active facet selection. */
export function applyFacetFilters<T>(
  entities: T[],
  facets: FacetConfig<T>[],
  state: FacetFilterState,
  matchSearch: (entity: T, query: string) => boolean,
): T[] {
  return entities.filter((e) => {
    if (state.search && !matchSearch(e, state.search)) return false;
    for (const f of facets) {
      const sel = selectionOf(state, f.key);
      if (sel.length && !sel.includes(f.keyOf(e))) return false;
    }
    return true;
  });
}

/** Count one facet over entities narrowed by every OTHER active facet + search. */
function facetCounts<T>(
  entities: T[],
  facets: FacetConfig<T>[],
  state: FacetFilterState,
  facet: FacetConfig<T>,
  matchSearch: (entity: T, query: string) => boolean,
): Map<string, number> {
  const others: FacetFilterState = { ...state, selections: { ...state.selections, [facet.key]: [] } };
  const base = applyFacetFilters(entities, facets, others, matchSearch);
  const counts = new Map<string, number>();
  for (const e of base) {
    const k = facet.keyOf(e);
    if (!k) continue;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return counts;
}

interface FacetSectionProps {
  facet: FacetConfig<unknown>;
  counts: Map<string, number>;
  selected: string[];
  onToggle: (value: string) => void;
}

const FacetSection: React.FC<FacetSectionProps> = ({ facet, counts, selected, onToggle }) => {
  let keys = Array.from(counts.keys());
  if (facet.order) {
    const order = facet.order;
    keys = keys.sort((a, b) => order.indexOf(a) - order.indexOf(b));
  } else {
    keys = keys.sort((a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0));
  }
  if (keys.length === 0) return null;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10.5, px: 0.5 }}>
        {facet.title}
      </Typography>
      <Box sx={{ mt: 0.5 }}>
        {keys.map((k) => (
          <FormControlLabel
            key={k}
            sx={{ display: 'flex', m: 0, '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 } }}
            control={<Checkbox size="small" checked={selected.includes(k)} onChange={() => onToggle(k)} sx={{ py: 0.25 }} />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                {facet.iconOf?.(k)}
                <Typography noWrap sx={{ fontSize: 12.5, flex: 1 }}>{facet.labelOf ? facet.labelOf(k) : k}</Typography>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', flexShrink: 0 }}>{counts.get(k) ?? 0}</Typography>
              </Box>
            }
          />
        ))}
      </Box>
    </Box>
  );
};

interface FacetedFilterRailProps<T> {
  entities: T[];
  facets: FacetConfig<T>[];
  state: FacetFilterState;
  setState: React.Dispatch<React.SetStateAction<FacetFilterState>>;
  matchSearch: (entity: T, query: string) => boolean;
  searchPlaceholder?: string;
  /**
   * Opt-in explicit-submit mode. When provided, typing no longer filters live —
   * the rail renders a Search button and submits on Enter, calling this with the
   * current query. Use for slow/expensive (e.g. server-side) searches.
   */
  onSearchSubmit?: (query: string) => void;
  /** Shows a spinner on the Search button while a submitted search runs. */
  searchLoading?: boolean;
}

function FacetedFilterRail<T>({
  entities, facets, state, setState, matchSearch, searchPlaceholder = 'Search…',
  onSearchSubmit, searchLoading = false,
}: FacetedFilterRailProps<T>): React.ReactElement {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const toggle = (key: string) => (value: string) =>
    setState((prev) => {
      const cur = prev.selections[key] ?? [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...prev, selections: { ...prev.selections, [key]: next } };
    });

  const primary = facets.filter((f) => !f.advanced);
  const advanced = facets.filter((f) => f.advanced);

  const activeCount =
    facets.reduce((n, f) => n + selectionOf(state, f.key).length, 0) + (state.search ? 1 : 0);

  const renderFacet = (f: FacetConfig<T>) => (
    <FacetSection
      key={f.key}
      facet={f as FacetConfig<unknown>}
      counts={facetCounts(entities, facets, state, f, matchSearch)}
      selected={selectionOf(state, f.key)}
      onToggle={toggle(f.key)}
    />
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', overflow: 'hidden' }}>
      <Box sx={{ p: 1.5, flexShrink: 0 }}>
        <TextField
          fullWidth size="small" placeholder={searchPlaceholder}
          value={state.search}
          onChange={(e) => setState((prev) => ({ ...prev, search: e.target.value }))}
          onKeyDown={onSearchSubmit ? (e) => { if (e.key === 'Enter') onSearchSubmit(state.search); } : undefined}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        {onSearchSubmit && (
          <Button
            fullWidth size="small" variant="outlined" disabled={searchLoading}
            onClick={() => onSearchSubmit(state.search)}
            startIcon={searchLoading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon fontSize="small" />}
            sx={{ mt: 0.75, textTransform: 'none' }}
          >
            {searchLoading ? 'Searching…' : 'Search'}
          </Button>
        )}
        <Box sx={{ height: 28, mt: 0.5, display: 'flex', alignItems: 'center' }}>
          {activeCount > 0 && (
            <Button
              size="small" sx={{ textTransform: 'none', py: 0 }}
              onClick={() => { setState(emptyFacetState()); onSearchSubmit?.(''); }}
            >
              Clear all ({activeCount})
            </Button>
          )}
        </Box>
      </Box>
      <Divider />

      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
        {primary.map(renderFacet)}

        {advanced.length > 0 && (
          <>
            <Button
              size="small" fullWidth onClick={() => setAdvancedOpen((o) => !o)}
              endIcon={advancedOpen ? <ExpandLess /> : <ExpandMore />}
              sx={{ justifyContent: 'space-between', textTransform: 'none', color: 'text.secondary', mt: 0.5 }}
            >
              Advanced
            </Button>
            <Collapse in={advancedOpen}>
              <Box sx={{ mt: 1 }}>{advanced.map(renderFacet)}</Box>
            </Collapse>
          </>
        )}
      </Box>
    </Box>
  );
}

export default FacetedFilterRail;
